import axios from 'axios';
import type { CompletedPart, PartPresignedUrl } from './multipart-upload-api';
import { S3UploadError, classifyAxiosError } from './s3-upload-error';

/** 동시 업로드 수 제한 */
const DEFAULT_MAX_CONCURRENCY = 5;
/** 대용량 파일은 브라우저/사용자망 부담을 줄이기 위해 병렬 수를 낮춘다 */
const LARGE_FILE_MAX_CONCURRENCY = 3;
export const LARGE_FILE_MULTIPART_THRESHOLD_BYTES = 512 * 1024 * 1024;
/** 파트 업로드 실패 시 최대 재시도 횟수 */
const MAX_RETRIES = 3;
const ONLINE_WAIT_TIMEOUT_MS = 30_000;

interface MultipartUploadToS3Params {
  file: File;
  partPresignedUrls: PartPresignedUrl[];
  partSize: number;
  onProgress?: (progress: number) => void;
}

export function getMultipartConcurrency(fileSize: number): number {
  return fileSize >= LARGE_FILE_MULTIPART_THRESHOLD_BYTES
    ? LARGE_FILE_MAX_CONCURRENCY
    : DEFAULT_MAX_CONCURRENCY;
}

export async function waitForOnlineBeforeRetry({
  getOnline = () =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  addEventListener = (type, listener, options) =>
    window.addEventListener(type, listener, options),
  removeEventListener = (type, listener, options) =>
    window.removeEventListener(type, listener, options),
  timeoutMs = ONLINE_WAIT_TIMEOUT_MS,
}: {
  getOnline?: () => boolean;
  addEventListener?: (
    type: 'online',
    listener: EventListener,
    options?: AddEventListenerOptions,
  ) => void;
  removeEventListener?: (
    type: 'online',
    listener: EventListener,
    options?: EventListenerOptions,
  ) => void;
  timeoutMs?: number;
} = {}): Promise<void> {
  if (getOnline()) {
    return;
  }

  await new Promise<void>((resolve) => {
    const cleanup = () => {
      removeEventListener('online', handleOnline);
      globalThis.clearTimeout(timeoutId);
    };
    const handleOnline = () => {
      cleanup();
      resolve();
    };
    const timeoutId = globalThis.setTimeout(() => {
      cleanup();
      resolve();
    }, timeoutMs);

    addEventListener('online', handleOnline, { once: true });
  });
}

/**
 * S3 Multipart Upload 실행
 *
 * - 파일을 partSize 단위로 슬라이스
 * - MAX_CONCURRENCY(5)개 병렬 업로드
 * - 실패 시 MAX_RETRIES(3)까지 재시도 (exponential backoff)
 * - 전체 진행률 추적
 *
 * 주의: S3 버킷 CORS 설정에 `ExposeHeaders: ["ETag"]`가 있어야
 *       응답에서 ETag 헤더를 읽을 수 있습니다.
 */
export async function uploadMultipartToS3({
  file,
  partPresignedUrls,
  partSize,
  onProgress,
}: MultipartUploadToS3Params): Promise<CompletedPart[]> {
  const totalSize = file.size;
  const maxConcurrency = getMultipartConcurrency(totalSize);
  const completedParts: CompletedPart[] = [];
  const partProgress: Map<number, number> = new Map();
  const abortController = new AbortController();
  let retryingPartCount = 0;
  let releaseRetryBarrier: (() => void) | null = null;
  let retryBarrier: Promise<void> = Promise.resolve();

  const beginRetry = () => {
    if (retryingPartCount === 0) {
      retryBarrier = new Promise<void>((resolve) => {
        releaseRetryBarrier = resolve;
      });
    }
    retryingPartCount += 1;
  };

  const endRetry = () => {
    retryingPartCount = Math.max(0, retryingPartCount - 1);
    if (retryingPartCount === 0) {
      releaseRetryBarrier?.();
      releaseRetryBarrier = null;
    }
  };

  // 전체 진행률 업데이트
  const updateTotalProgress = () => {
    let totalLoaded = 0;
    for (const loaded of partProgress.values()) {
      totalLoaded += loaded;
    }
    const percent = Math.round((totalLoaded * 100) / totalSize);
    // 100은 complete API 호출 후에 설정
    onProgress?.(Math.min(percent, 99));
  };

  // 단일 파트 업로드 (재시도 포함)
  const uploadPart = async (part: PartPresignedUrl): Promise<CompletedPart> => {
    const start = (part.partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    const chunk = file.slice(start, end);
    let isRetrying = false;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const response = await axios.put(part.presignedUrl, chunk, {
          signal: abortController.signal,
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.loaded) {
              partProgress.set(part.partNumber, progressEvent.loaded);
              updateTotalProgress();
            }
          },
        });

        // ETag 추출 (S3 PUT 응답 헤더에 포함)
        // CORS 설정에 ExposeHeaders: ["ETag"]가 필요
        const eTag = response.headers['etag'] || response.headers['ETag'];
        if (!eTag) {
          throw new Error(
            `ETag not found in response for part ${part.partNumber}. S3 CORS에 ExposeHeaders: ["ETag"] 설정이 필요합니다.`,
          );
        }

        const completedPart = {
          partNumber: part.partNumber,
          eTag: eTag.replace(/"/g, ''), // 따옴표 제거
        };
        if (isRetrying) endRetry();
        return completedPart;
      } catch (error) {
        if (abortController.signal.aborted) {
          if (isRetrying) endRetry();
          throw error;
        }
        if (attempt < MAX_RETRIES - 1) {
          if (!isRetrying) {
            beginRetry();
            isRetrying = true;
          }
          if (classifyAxiosError(error) === 'NETWORK') {
            await waitForOnlineBeforeRetry();
          }
          // exponential backoff: 1초, 2초, 4초
          const delay = Math.pow(2, attempt) * 1000;
          await new Promise((resolve) => setTimeout(resolve, delay));
          // 재시도 시 진행률 리셋
          partProgress.set(part.partNumber, 0);
          continue;
        }
        abortController.abort();
        if (isRetrying) endRetry();
        const reason = classifyAxiosError(error);
        const msg = error instanceof Error ? error.message : String(error);
        throw new S3UploadError(
          `Part ${part.partNumber} 업로드 실패: ${msg}`,
          reason,
        ); // 모든 재시도 소진
      }
    }

    // 타입 안전을 위한 unreachable 처리
    throw new Error(`Part ${part.partNumber} 업로드 실패: 모든 재시도 소진`);
  };

  // 고정 worker pool을 사용해 첫 실패 시 새 파트를 시작하지 않고,
  // 이미 진행 중인 요청도 취소한 뒤 모두 settle될 때까지 기다린다.
  // S3 AbortMultipartUpload가 in-flight UploadPart와 경합하면 잔여 파트가
  // 남을 수 있으므로 호출자에게 실패를 반환하기 전에 반드시 정리한다.
  let nextPartIndex = 0;
  let primaryError: unknown;
  const workerCount = Math.min(maxConcurrency, partPresignedUrls.length);
  const workers = Array.from({ length: workerCount }, async () => {
    while (!abortController.signal.aborted) {
      await retryBarrier;
      if (abortController.signal.aborted) return;
      const partIndex = nextPartIndex;
      nextPartIndex += 1;
      const part = partPresignedUrls[partIndex];
      if (!part) return;
      try {
        completedParts.push(await uploadPart(part));
      } catch (error) {
        if (primaryError === undefined) {
          primaryError = error;
          abortController.abort();
        }
        return;
      }
    }
  });

  await Promise.allSettled(workers);
  if (primaryError !== undefined) {
    throw primaryError;
  }

  // partNumber 순으로 정렬 (S3 CompleteMultipartUpload 요구사항)
  completedParts.sort((a, b) => a.partNumber - b.partNumber);

  return completedParts;
}
